import { useCallback } from 'react';
import dayjs from 'dayjs';
import toast from 'shared/utils/toast';
import { t } from 'i18next';

interface UseGridUpdateParams {
  service: any;
  refreshColumnsData: () => void;
  apiRef: any;
  disableEdit?: boolean;
}

export function useGridUpdate({
  service,
  refreshColumnsData,
  apiRef,
  disableEdit = false
}: UseGridUpdateParams) {
  const handleProcessRowUpdate = useCallback(
    async (newRow: any, oldRow: any) => {
      if (disableEdit) return oldRow;

      const gridApi = apiRef?.current;

      const field = Object.keys(newRow).find((key) => newRow[key] !== oldRow[key]);
      if (!field) return newRow;

      const value = newRow[field];
      const oldValue = oldRow[field];

      const toDecimalString = (val: any) => {
        if (typeof val !== 'string') return val;
        const trimmed = val.trim();
        if (trimmed === '' || !isNaN(Number(trimmed))) {
          return val;
        }
        const italianFormatted = trimmed.replace(/\./g, '').replace(/,/g, '.');
        if (!isNaN(Number(italianFormatted))) {
          return italianFormatted;
        }
        return val;
      };

      const getColumnType = (fieldName: string): string | undefined => {
        const col =
          typeof gridApi?.getColumn === 'function' ? gridApi.getColumn(fieldName) : undefined;
        return col?.type;
      };

      const formatDateForField = (val: any, fieldName: string) => {
        if (val instanceof Date || dayjs.isDayjs(val)) {
          const colType = getColumnType(fieldName);
          if (colType === 'dateTime') return dayjs(val).format('YYYY-MM-DD HH:mm');
          if (colType === 'date') return dayjs(val).format('YYYY-MM-DD');
          return dayjs(val).format(fieldName === 'timestamp' ? 'DD/MM/YYYY' : 'YYYY-MM-DD');
        }
        return toDecimalString(val);
      };

      const normalize = (val: any, fieldName: string) => {
        if (val instanceof Date || dayjs.isDayjs(val)) {
          return dayjs(val).format(fieldName === 'timestamp' ? 'DD/MM/YYYY' : 'YYYY-MM-DD');
        }
        return toDecimalString(val);
      };

      const valuesEqual = (a: any, b: any, fieldName: string) => {
        if (a == null && b == null) return true;
        if (a == null || b == null) return false;
        const colType = getColumnType(fieldName);
        if (colType === 'dateTime' || colType === 'date') {
          const da = dayjs(a);
          const db = dayjs(b);
          if (da.isValid() && db.isValid()) {
            return da.valueOf() === db.valueOf();
          }
        }
        return normalize(a, fieldName) === normalize(b, fieldName);
      };

      if (valuesEqual(value, oldValue, field)) {
        return newRow;
      }
      let editableFields: string[] = [];

      if (gridApi && typeof gridApi.getAllColumns === 'function') {
        editableFields = gridApi
          .getAllColumns()
          .filter((col: any) => col.editable === true)
          .map((col: any) => col.field);
      }

      const payload: Record<string, any> = {};

      Object.keys(newRow).forEach((key) => {
        if (key === field) {
          payload[key] = formatDateForField(value, key);
          return;
        }

        const currentVal = newRow[key];

        if (
          !editableFields.includes(key) &&
          typeof currentVal === 'string' &&
          isNaN(Number(currentVal))
        ) {
          return;
        }

        if (
          currentVal &&
          typeof currentVal === 'object' &&
          !(currentVal instanceof Date) &&
          !dayjs.isDayjs(currentVal)
        ) {
          if ('id' in currentVal) {
            payload[key] = currentVal.id;
          }
        } else {
          payload[key] = formatDateForField(currentVal, key);
        }
      });

      try {
        const updatedRow = await service.update(newRow.id, payload);

        toast.success(t('toasts.updated-cell'));

        // In grouped detail grids, refreshing after timestamp changes can reuse stale
        // grouping params and temporarily empty the table.
        if (field !== 'timestamp') {
          refreshColumnsData();
        }

        return updatedRow || newRow;
      } catch (error) {
        toast.error('Errore durante l’aggiornamento');
        return oldRow;
      }
    },
    [service, refreshColumnsData, apiRef, disableEdit]
  );

  return { handleProcessRowUpdate };
}
